/**
 * Handlebars Template Protection Utilities
 * 
 * Protects Handlebars syntax ({{...}}, {{#each}}, {{/each}}, etc.) from being
 * corrupted by contentEditable browser normalization during visual editing.
 * 
 * Strategy: 
 * - Block helpers ({{#each}}, {{/each}}, {{#if}}, etc.) are converted to HTML comments
 * - Regular variables ({{variable}}) are replaced with placeholder spans
 */

const HANDLEBARS_PLACEHOLDER_PREFIX = 'hbs-placeholder-';
let placeholderCounter = 0;

interface PlaceholderMap {
  [key: string]: string;
}

// Regex patterns for Handlebars block helpers
const BLOCK_HELPER_OPEN_PATTERN = /\{\{#(each|if|unless|with|let|inline|block|contentFor|extend|embed|log|lookup)\s+[^}]*\}\}/g;
const BLOCK_HELPER_CLOSE_PATTERN = /\{\{\/(each|if|unless|with|let|inline|block|contentFor|extend|embed|log|lookup)\}\}/g;
const BLOCK_HELPER_ELSE_PATTERN = /\{\{else\}\}/g;

/**
 * Convert Handlebars syntax to protected format
 * - Block helpers ({{#each}}, {{/each}}, etc.) → HTML comments
 * - Regular variables ({{variable}}) → placeholder spans
 * @param html - Raw HTML with Handlebars syntax
 * @returns HTML with protected syntax + mapping object
 */
export function protectHandlebars(html: string): { html: string; map: PlaceholderMap } {
  const map: PlaceholderMap = {};
  placeholderCounter = 0;

  let protectedHtml = html;

  // Step 1: Convert block helper opening tags to HTML comments (only if not already commented)
  // e.g., {{#each items}} → <!--{{#each items}}-->
  // Skip if already in comment: <!--{{#each items}}--> stays as is
  protectedHtml = protectedHtml.replace(BLOCK_HELPER_OPEN_PATTERN, (match, p1, offset, string) => {
    // Check if this match is already inside an HTML comment
    const beforeMatch = string.substring(0, offset);
    const lastCommentStart = beforeMatch.lastIndexOf('<!--');
    const lastCommentEnd = beforeMatch.lastIndexOf('-->');
    
    // If we're inside a comment (comment start is more recent than comment end), don't re-comment
    if (lastCommentStart > lastCommentEnd) {
      return match;
    }
    
    return `<!--${match}-->`;
  });

  // Step 2: Convert block helper closing tags to HTML comments (only if not already commented)
  // e.g., {{/each}} → <!--{{/each}}-->
  protectedHtml = protectedHtml.replace(BLOCK_HELPER_CLOSE_PATTERN, (match, p1, offset, string) => {
    // Check if this match is already inside an HTML comment
    const beforeMatch = string.substring(0, offset);
    const lastCommentStart = beforeMatch.lastIndexOf('<!--');
    const lastCommentEnd = beforeMatch.lastIndexOf('-->');
    
    // If we're inside a comment, don't re-comment
    if (lastCommentStart > lastCommentEnd) {
      return match;
    }
    
    return `<!--${match}-->`;
  });

  // Step 3: Convert {{else}} to HTML comments (only if not already commented)
  // e.g., {{else}} → <!--{{else}}-->
  protectedHtml = protectedHtml.replace(BLOCK_HELPER_ELSE_PATTERN, (match, offset, string) => {
    // Check if this match is already inside an HTML comment
    const beforeMatch = string.substring(0, offset);
    const lastCommentStart = beforeMatch.lastIndexOf('<!--');
    const lastCommentEnd = beforeMatch.lastIndexOf('-->');
    
    // If we're inside a comment, don't re-comment
    if (lastCommentStart > lastCommentEnd) {
      return match;
    }
    
    return `<!--${match}-->`;
  });

  // Step 4: Protect remaining Handlebars expressions (regular variables) with placeholder spans
  // This will NOT match block helpers since they're already converted to comments
  // Also skip if already inside a placeholder span or inside an HTML tag attribute
  protectedHtml = protectedHtml.replace(/(\{\{[^}]*\}\})/g, (match, _p1, offset, string) => {
    // Check if already inside an HTML comment
    const beforeMatch = string.substring(0, offset);
    const lastCommentStart = beforeMatch.lastIndexOf('<!--');
    const lastCommentEnd = beforeMatch.lastIndexOf('-->');
    
    if (lastCommentStart > lastCommentEnd) {
      return match;
    }
    
    // Check if already inside a placeholder span
    const lastSpanStart = beforeMatch.lastIndexOf('<span class="hbs-placeholder"');
    const lastSpanEnd = beforeMatch.lastIndexOf('</span>');
    
    if (lastSpanStart > lastSpanEnd) {
      return match;
    }

    // Check if inside an HTML tag attribute (between < and >)
    // e.g. src="{{qr_code}}" — we must NOT wrap this in a span
    const lastTagOpen = beforeMatch.lastIndexOf('<');
    const lastTagClose = beforeMatch.lastIndexOf('>');
    if (lastTagOpen > lastTagClose) {
      // We're inside a tag — leave the handlebars expression untouched
      return match;
    }
    
    const id = `${HANDLEBARS_PLACEHOLDER_PREFIX}${placeholderCounter++}`;
    map[id] = match;
    
    // Use a span with data attribute to store the placeholder
    // contentEditable won't break this structure
    return `<span class="hbs-placeholder" data-hbs-id="${id}" contenteditable="false" >${match}</span>`;
  });

  return { html: protectedHtml, map };
}

/**
 * Restore Handlebars syntax from protected format
 * - HTML comments → Block helpers
 * - Placeholder spans → Regular variables
 * @param html - HTML with protected syntax
 * @param map - Mapping of placeholder IDs to original Handlebars syntax
 * @returns Original HTML with Handlebars syntax restored
 */
export function restoreHandlebars(html: string, map?: PlaceholderMap): string {
  let restoredHtml = html;

  // Step 1: Restore block helpers from HTML comments
  // e.g., <!--{{#each items}}--> → {{#each items}}
  restoredHtml = restoredHtml.replace(/<!--(\{\{#(?:each|if|unless|with|let|inline|block|contentFor|extend|embed|log|lookup)\s+[^}]*\}\})-->/g, (match, captured) => {
    return captured;
  });

  // Step 2: Restore closing block helpers from HTML comments
  // e.g., <!--{{/each}}--> → {{/each}}
  restoredHtml = restoredHtml.replace(/<!--(\{\{\/(?:each|if|unless|with|let|inline|block|contentFor|extend|embed|log|lookup)\}\})-->/g, (match, captured) => {
    return captured;
  });

  // Step 3: Restore {{else}} from HTML comments
  // e.g., <!--{{else}}--> → {{else}}
  restoredHtml = restoredHtml.replace(/<!--(\{\{else\}\})-->/g, (match, captured) => {
    return captured;
  });

  // Step 4: Restore regular variables from placeholder spans
  // If no map provided, extract from data attributes
  if (!map) {
    map = {};
    const regex = /<span[^>]*data-hbs-id="([^"]+)"[^>]*>([^<]+)<\/span>/g;
    let match;
    while ((match = regex.exec(restoredHtml)) !== null) {
      map[match[1]] = match[2];
    }
  }

  // Replace placeholder spans with original Handlebars syntax
  restoredHtml = restoredHtml.replace(
    /<span[^>]*class="hbs-placeholder"[^>]*data-hbs-id="([^"]+)"[^>]*>.*?<\/span>/g,
    (match, id) => {
      return map![id] || match;
    }
  );

  return restoredHtml;
}

/**
 * Check if HTML contains Handlebars syntax
 * @param html - HTML string to check
 * @returns true if Handlebars syntax is found
 */
export function containsHandlebars(html: string): boolean {
  return /\{\{[^}]*\}\}/.test(html);
}

/**
 * Auto-protect Handlebars in contentEditable on load
 * Call this when loading HTML into the editor
 */
export function prepareForEditing(html: string): { html: string; map: PlaceholderMap } {
  if (containsHandlebars(html)) {
    return protectHandlebars(html);
  }
  return { html, map: {} };
}

/**
 * Auto-restore Handlebars when getting HTML from editor
 * Call this when saving/exporting HTML from the editor
 */
export function prepareForSaving(html: string, map?: PlaceholderMap): string {
  return restoreHandlebars(html, map);
}
